import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Form,
  Col,
  Row,
  Spinner,
  Alert,
} from "react-bootstrap";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import useRealtimeReadOnlyList from "../../realtime/useRealtimeReadOnlyList";

const ScheduleChangeLog = () => {
  const apiUrls = useSelector(selectApiUrls);

  const {
    data: changeLogData,
    error: changeLogError,
    isLoading,
    isFetching,
    refetch,
  } = useRealtimeReadOnlyList({ url: apiUrls?.scheduleChangeLog });

  const changeLogs = useMemo(
    () => (Array.isArray(changeLogData) ? changeLogData : []),
    [changeLogData]
  );
  const errorMessage =
    changeLogError?.message ||
    changeLogError?.data?.message ||
    (changeLogError ? "Failed to fetch schedule change log data" : null);

  const [filteredLog, setFilteredLog] = useState([]);
  const [userFilter, setUserFilter] = useState(null);
  const [jobNumberFilter, setJobNumberFilter] = useState(null);
  const [fieldFilter, setFieldFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [showCompactFilters, setShowCompactFilters] = useState(false);

  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueJobNumbers, setUniqueJobNumbers] = useState([]);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "BLANK";
    }
    if (value === false || value === "false" || value === "Off" || value === "No") {
      return "No";
    }
    if (value === true || value === "true" || value === "On" || value === "Yes") {
      return "Yes";
    }
    return value;
  };

  // Format field names for better display
  const formatFieldName = (field) => {
    if (!field) return "";
    return field
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
      .replace(/^[a-z]/, (match) => match.toUpperCase()); // Capitalize the first letter
  };

  // Extract unique filter values
  useEffect(() => {
    if (changeLogs.length) {
      setUniqueUsers([...new Set(changeLogs.map((item) => item.CurrentUser))]);
      setUniqueJobNumbers([...new Set(changeLogs.map((item) => item.JobNumber))]);
      setUniqueFields([...new Set(changeLogs.map((item) => formatFieldName(item.Field)))]);
      setUniqueRoles([...new Set(changeLogs.map((item) => item.UserRole))]);
    } else {
      setUniqueUsers([]);
      setUniqueJobNumbers([]);
      setUniqueFields([]);
      setUniqueRoles([]);
    }
  }, [changeLogs]);

  // Apply filters
  useEffect(() => {
    if (!changeLogs.length) {
      setFilteredLog([]);
      return;
    }

    // Sort changeLogs by DateTime descending to have the most recent at the top
    let sortedLog = [...changeLogs].sort(
      (a, b) => new Date(b.DateTime) - new Date(a.DateTime)
    );

    let filtered = sortedLog;

    if (userFilter) {
      filtered = filtered.filter((item) => item.CurrentUser === userFilter);
    }

    if (jobNumberFilter) {
      filtered = filtered.filter((item) => item.JobNumber === jobNumberFilter);
    }

    if (fieldFilter) {
      filtered = filtered.filter(
        (item) => formatFieldName(item.Field) === fieldFilter
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((item) => item.UserRole === roleFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter((item) => {
        const itemDate = moment.utc(item.DateTime);
        const startDate = moment.utc(dateRange[0], "YYYY-MM-DD").startOf("day");
        const endDate = moment.utc(dateRange[1], "YYYY-MM-DD").endOf("day");
        return itemDate.isBetween(startDate, endDate, "minutes", "[]");
      });
    }

    setFilteredLog(filtered);
  }, [
    changeLogs,
    userFilter,
    jobNumberFilter,
    fieldFilter,
    roleFilter,
    searchTerm,
    dateRange,
  ]);

  const clearFilters = () => {
    setUserFilter(null);
    setJobNumberFilter(null);
    setFieldFilter(null);
    setRoleFilter(null);
    setSearchTerm("");
    setDateRange([null, null]);
  };

  return (
    <div className="schedule-change-log-container">
      {/* Header */}
      <div className="schedule-section-heading d-flex justify-content-between align-items-center mb-3">
        <h4>Schedule Change Log</h4>
        <div className="schedule-section-actions d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="schedule-filter-toggle"
            onClick={() => setShowCompactFilters(isOpen => !isOpen)}
            aria-expanded={showCompactFilters}
            aria-controls="schedule-change-log-filters"
          >
            <i className="bi bi-funnel me-1"></i>
            Filters
          </Button>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading || isFetching || !apiUrls?.scheduleChangeLog}
          >
            {isLoading || isFetching ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Refreshing...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-repeat me-1"></i>
                Refresh
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div
        id="schedule-change-log-filters"
        className={`controls-section schedule-filter-panel mb-3 ${showCompactFilters ? 'is-open' : ''}`}
        style={{ padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "0.375rem" }}
      >
        <Form>
          <Row className="g-3">
            {/* First Row of Filters */}
            <Col md={3}>
              <Form.Group controlId="userFilter">
                <Form.Label>User Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setUserFilter(e.target.value || null)}
                  value={userFilter || ""}
                >
                  <option value="">All Users</option>
                  {uniqueUsers.map((user, index) => (
                    <option key={index} value={user}>
                      {user}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="jobNumberFilter">
                <Form.Label>Job Number Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setJobNumberFilter(e.target.value || null)}
                  value={jobNumberFilter || ""}
                >
                  <option value="">All Job Numbers</option>
                  {uniqueJobNumbers.map((jobNumber, index) => (
                    <option key={index} value={jobNumber}>
                      {jobNumber}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="fieldFilter">
                <Form.Label>Field Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setFieldFilter(e.target.value || null)}
                  value={fieldFilter || ""}
                >
                  <option value="">All Fields</option>
                  {uniqueFields.map((field, index) => (
                    <option key={index} value={field}>
                      {field}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="roleFilter">
                <Form.Label>Role Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setRoleFilter(e.target.value || null)}
                  value={roleFilter || ""}
                >
                  <option value="">All Roles</option>
                  {uniqueRoles.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Second Row of Filters */}
          <Row className="g-3 mt-2">
            <Col md={3}>
              <Form.Group controlId="dateRangeStart">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange[0] || ""}
                  onChange={(e) => setDateRange([e.target.value || null, dateRange[1]])}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="dateRangeEnd">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange[1] || ""}
                  onChange={(e) => setDateRange([dateRange[0], e.target.value || null])}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="searchTerm">
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search all fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Results Summary */}
      <div className="mb-3">
        <span className="text-muted">
          Showing {filteredLog.length} of {changeLogs.length} entries
        </span>
      </div>

      {/* Error Display */}
      {errorMessage && (
        <Alert variant="danger" className="mb-3">
          {errorMessage}
        </Alert>
      )}

      {/* Loading / Data Display */}
      {isLoading ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading schedule change log...</p>
        </div>
      ) : filteredLog.length === 0 ? (
        <Alert variant="info">
          {changeLogs.length === 0 ? "No schedule changes found" : "No changes match the current filters"}
        </Alert>
      ) : (
        <div style={{ maxHeight: "600px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "0.375rem" }}>
          <Table
            striped
            bordered
            hover
            responsive
            className="mb-0 schedule-responsive-table"
          >
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                <th>Date/Time</th>
                <th>User</th>
                <th>Role</th>
                <th>Job Number</th>
                <th>Job Name</th>
                <th>Field Changed</th>
                <th>Previous Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredLog.map((item, index) => (
                <tr key={item.ID || index}>
                  <td data-label="Date/Time" style={{ whiteSpace: "nowrap" }}>
                    {moment(item.DateTime).format("MM/DD/YYYY hh:mm A")}
                  </td>
                  <td data-label="User">{formatValue(item.CurrentUser)}</td>
                  <td data-label="Role">{formatValue(item.UserRole)}</td>
                  <td data-label="Job Number" style={{ whiteSpace: "nowrap" }}>{formatValue(item.JobNumber)}</td>
                  <td data-label="Job Name">{formatValue(item.JobName)}</td>
                  <td data-label="Field Changed">{formatFieldName(item.Field)}</td>
                  <td data-label="Previous Value">{formatValue(item.PrevValue)}</td>
                  <td data-label="New Value">{formatValue(item.NewValue)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ScheduleChangeLog;
